import React from "react";
import { useNavigate } from "react-router-dom";
import { useMeeting } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Zap, Settings2, Cpu, ArrowRight, FileText, AlertTriangle } from "lucide-react";

const DashboardPage = () => {
  const navigate = useNavigate();
  const { currentMeeting, selectedAssets } = useMeeting();

  // Navigate to asset page in presentation mode by default
  const navigateToAsset = (path) => {
    navigate(`${path}?presentation=true`);
  };

  const assetCards = [
    {
      id: "transformers",
      icon: Zap,
      title: "Transformers",
      description: "Power & distribution transformer diagnostics including DGA, SFRA, and continuous monitoring solutions.",
      path: "/assets/transformers",
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      failures: ["Winding Deformation", "Bushing Breakdown", "Insulation Aging"]
    },
    {
      id: "switchgear",
      icon: Settings2,
      title: "Switchgear",
      description: "Circuit breaker and switchgear analysis including contact resistance, partial discharge, and timing tests.",
      path: "/assets/switchgear",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      failures: ["Contact Wear", "Insulation Tracking", "Mechanism Failure"]
    },
    {
      id: "motors",
      icon: Cpu,
      title: "Motors",
      description: "Rotating machinery health assessment including vibration analysis, MCA, and current signature analysis.",
      path: "/assets/motors",
      color: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      failures: ["Bearing Seizure", "Stator Short", "Rotor Bar Crack"]
    }
  ];

  const getSelectedCount = (assetType) => {
    return selectedAssets.filter((a) => a.asset === assetType).length;
  };

  if (!currentMeeting) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" strokeWidth={1.5} />
        <h2 className="text-xl font-semibold text-heading text-slate-900 mb-2">No Active Session</h2>
        <p className="text-slate-500 mb-6">Please start a new session from the landing page.</p>
        <Button onClick={() => navigate("/landing")} className="bg-blue-600 hover:bg-blue-700">
          Go to Landing Page
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-heading text-slate-900 mb-2">Asset Dashboard</h1>
        <p className="text-slate-500">
          Presenting to <span className="font-medium text-slate-700">{currentMeeting.customer_name}</span> from{" "}
          <span className="font-medium text-slate-700">{currentMeeting.company}</span>
        </p>
        <p className="text-sm text-blue-600 mt-1">
          Primary focus: {currentMeeting.primary_asset_focus}
        </p>
      </div>

      {/* Asset Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        {assetCards.map((asset) => {
          const Icon = asset.icon;
          const selectedCount = getSelectedCount(asset.id);
          const isPrimary = currentMeeting.primary_asset_focus === asset.title;

          return (
            <Card
              key={asset.id}
              className={`asset-card group cursor-pointer ${isPrimary ? "ring-2 ring-blue-500" : ""}`}
              onClick={() => navigateToAsset(asset.path)}
              data-testid={`asset-card-${asset.id}`}
            >
              {/* Icon Header */}
              <div className={`relative h-32 overflow-hidden ${asset.bgColor} border-b ${asset.borderColor} flex items-center justify-center`}>
                <Icon className={`w-16 h-16 ${asset.color} opacity-80 group-hover:scale-110 transition-transform duration-300`} strokeWidth={1} />
                {isPrimary && (
                  <div className="absolute top-3 right-3 px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded">
                    Primary Focus
                  </div>
                )}
                {selectedCount > 0 && (
                  <div className="absolute top-3 left-3 px-2 py-1 bg-green-600 text-white text-xs font-medium rounded">
                    {selectedCount} selected
                  </div>
                )}
              </div>

              {/* Content */}
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 ${asset.bgColor} rounded-md flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${asset.color}`} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-semibold text-heading text-slate-900">{asset.title}</h3>
                </div>
                
                <p className="text-slate-500 text-sm mb-4">{asset.description}</p>

                {/* Failure Modes Preview */}
                <div className="mb-4">
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">
                    Key Failure Modes
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {asset.failures.map((failure, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded"
                      >
                        {failure}
                      </span>
                    ))}
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full group-hover:bg-blue-50 group-hover:border-blue-500 group-hover:text-blue-600 transition-colors"
                  data-testid={`deep-dive-${asset.id}`}
                >
                  Deep Dive
                  <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Summary CTA */}
      <div className="flex justify-center">
        <Button
          onClick={() => navigate("/summary")}
          size="lg"
          className="bg-blue-900 hover:bg-blue-800"
          data-testid="generate-summary-btn"
        >
          <FileText className="w-5 h-5 mr-2" strokeWidth={1.5} />
          Generate Summary
          {selectedAssets.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-white/20 rounded text-sm">
              {selectedAssets.length} items
            </span>
          )}
        </Button>
      </div>
    </div>
  );
};

export default DashboardPage;
